import * as Highcharts from "highcharts";
import { useMemo } from "react";
import { getLastElement } from "../../../utility/ArrayUtility";
import {
  formatWithLeadingZero,
  subtractHours,
} from "../../../utility/DateUtility";
import { FormatEggs } from "../../../utility/EggUtility";
import { t } from "../../../utility/TranslateUtility";
import { BarChartSettingsHour } from "../../Charts/ChartSettings/BarChartSettings";
import HighchartsFullscreenWrapper from "../Wrappers/HighchartsFullscreenWrapper";

export type MachineSpeedHourWidgetPresentationProps = {
  awaitingResponse: boolean;
  data: MachineSpeedHourApiData;
};

export type MachineSpeedHourApiResponse = {
  data: MachineSpeedHourApiData;
};

export type MachineSpeedHourApiData = {
  machineSpeedHourPeriods: Array<MachineSpeedHourPeriod>;
};

export type MachineSpeedHourPeriod = {
  dateTime: Date;
  eggsPerHour: number;
};

const ChartSettings = {
  ...BarChartSettingsHour,
  yAxis: {
    title: {
      text: t("widgets.machineSpeedHour.yAxisTitle"),
    },
  },
  legend: {
    enabled: false,
  },
  tooltip: {
    formatter: function (this: any) {
      return (
        "<b>" +
        t("widgets.machineSpeedHour.tooltipTime") +
        "</b>" +
        formatWithLeadingZero(new Date(this.point.x).getUTCHours()) +
        ":" +
        formatWithLeadingZero(new Date(this.point.x).getUTCMinutes()) +
        "<br/><b>" +
        t("widgets.machineSpeedHour.tooltipMeasure") +
        "</b> " +
        FormatEggs(this.point.y, null) +
        "</b> " +
        t("widgets.machineSpeedHour.tooltipUnit")
      );
    },
  },
};

function MapToChartData(input: MachineSpeedHourApiData) {
  const result = {
    data: input.machineSpeedHourPeriods.map(
      (machineSpeedHourPeriod: MachineSpeedHourPeriod) => ({
        x: machineSpeedHourPeriod.dateTime,
        y: machineSpeedHourPeriod.eggsPerHour,
      })
    ),
  };

  const last_element = getLastElement(input.machineSpeedHourPeriods);
  const newMax = last_element.dateTime;

  return {
    series: result,
    minDate: subtractHours(newMax, 30),
  };
}

export default function MachineSpeedHourWidgetPresentation({
  awaitingResponse,
  data,
}: MachineSpeedHourWidgetPresentationProps) {
  const newOptions = useMemo(() => {
    if (awaitingResponse || !data) return ChartSettings;

    const newChartData = MapToChartData(data);

    return {
      ...ChartSettings,
      xAxis: {
        ...ChartSettings.xAxis,
        min: newChartData.minDate,
      },
      series: newChartData.series,
    };
  }, [data, awaitingResponse]);

  return (
    <HighchartsFullscreenWrapper
      title={t("widgets.machineSpeedHour.title")}
      awaitingResponse={awaitingResponse}
      highcharts={Highcharts}
      chartOptions={newOptions}
    />
  );
}
